import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, FilePenLine, Search } from 'lucide-react';
import { MarketingShell } from '@/components/marketing-shell';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  TOOLS,
  TOOLS_BY_SLUG,
  TOOLS_INDEX_SEO,
  type ToolCategory,
} from './data';
import { ToolCard, ToolsGrid, visibleTools } from './components';
import { STUDIO_EDITOR_HREF, isEditorKeyword, keywordSuggestions } from './keyword-routing';
import { useTranslation } from '@/i18n';

import { Button, Input } from '@/components/ui';
import { setPageSeo } from '@/lib/brand';
import {
  trackToolsCategoryFilter,
  trackToolsSearch,
  trackToolsSearchResultOpened,
} from '@/lib/posthog';

type Filter = 'all' | ToolCategory;

/** Typing has stopped when no key arrives for this long; one `tools_search` per pause. */
export const SEARCH_EVENT_DELAY_MS = 800;

/** What the search events carry: the typed tool name, never more than 60 characters. */
function searchEventQuery(value: string): string {
  return value.trim().toLowerCase().slice(0, 60);
}

export function ToolsIndexPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const lastSearchEventQuery = useRef('');
  const category = searchParams.get('category');
  const activeCategory: Filter =
    category && (CATEGORY_ORDER as string[]).includes(category)
      ? (category as ToolCategory)
      : 'all';
  // The ad keyword (W.4.115). A keyword with one tool never reaches this page
  // (the Worker sends it to the tool); the rest get the editor card or picks.
  const adKeyword = searchParams.get('utm_term');
  const searching = searchQuery.trim() !== '';
  const showEditorCard = !searching && (activeCategory === 'edit' || isEditorKeyword(adKeyword));
  const keywordPicks =
    !searching && activeCategory === 'all'
      ? keywordSuggestions(adKeyword).flatMap((slug) => TOOLS_BY_SLUG[slug] ?? [])
      : [];

  const setActiveCategory = (value: Filter) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === 'all') next.delete('category');
        else next.set('category', value);
        return next;
      },
      { preventScrollReset: true },
    );
  };
  const chooseCategory = (value: Filter) => {
    if (value === activeCategory) return;
    setActiveCategory(value);
    trackToolsCategoryFilter({
      category: value,
      result_count: visibleTools(TOOLS, t, value, searchQuery).length,
    });
  };
  const resetFilters = () => {
    setSearchQuery('');
    setActiveCategory('all');
    document.getElementById('tools-search')?.focus();
  };
  const onToolOpen = (slug: string, position: number) => {
    const query = searchEventQuery(searchQuery);
    if (query) trackToolsSearchResultOpened({ query, tool: slug, position });
  };

  useEffect(() => {
    setPageSeo({
      title: t('tools.index.metaTitle', { defaultValue: TOOLS_INDEX_SEO.title }),
      description: t('tools.index.metaDescription', { defaultValue: TOOLS_INDEX_SEO.description }),
    });
  }, [t]);

  // W.4.115 (#2687): record what the visitor typed once typing stops, so a
  // search with no result shows a missing tool, synonym or ad keyword.
  useEffect(() => {
    const query = searchEventQuery(searchQuery);
    if (!query) {
      lastSearchEventQuery.current = '';
      return;
    }
    if (query === lastSearchEventQuery.current) return;
    const timer = window.setTimeout(() => {
      lastSearchEventQuery.current = query;
      const resultCount = visibleTools(TOOLS, t, activeCategory, searchQuery).length;
      trackToolsSearch({ query, result_count: resultCount, zero_results: resultCount === 0 });
    }, SEARCH_EVENT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery, activeCategory, t]);

  return (
    <MarketingShell surface="public-tools">
      <main className="pb-16 pt-8 text-foreground sm:pt-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <header className="mb-8">
            <h1 className="mb-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {t('tools.index.h1', { defaultValue: TOOLS_INDEX_SEO.h1 })}
            </h1>
            <p className="max-w-2xl text-base leading-7 text-muted-foreground">
              {t('tools.index.subhead', { defaultValue: TOOLS_INDEX_SEO.subhead })}
            </p>

            <div className="relative mb-4 mt-6 max-w-xl">
              <label htmlFor="tools-search" className="sr-only">
                {t('tools.index.searchLabel')}
              </label>
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="tools-search"
                type="search"
                placeholder={t('tools.index.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 w-full rounded-lg border border-input bg-card pl-12 pr-4 text-base text-foreground outline-none transition-all focus:border-primary focus:ring-2 focus:ring-ring"
              />
            </div>

            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={t('tools.index.categoriesLabel', { defaultValue: 'Filter by category' })}
            >
              {(['all', ...CATEGORY_ORDER] as Filter[]).map((cat) => (
                <Button
                  variant="unstyled"
                  key={cat}
                  type="button"
                  onClick={() => chooseCategory(cat)}
                  aria-pressed={activeCategory === cat}
                  className={
                    activeCategory === cat
                      ? 'inline-flex min-h-11 cursor-default items-center rounded-lg border border-primary bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                      : 'inline-flex min-h-11 items-center rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  }
                >
                  {t(
                    `tools.categories.${cat.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())}`,
                    { defaultValue: CATEGORY_LABELS[cat] },
                  )}
                </Button>
              ))}
            </div>
          </header>

          {showEditorCard ? <EditorCard /> : null}

          {keywordPicks.length > 0 ? (
            <section aria-labelledby="keyword-picks" className="mb-10">
              <h2 id="keyword-picks" className="text-2xl font-bold text-foreground">
                {t('tools.index.keywordPicks', { defaultValue: 'Start with one of these' })}
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {keywordPicks.map((tool) => (
                  <ToolCard key={tool.slug} tool={tool} surface="public" variant="public" />
                ))}
              </div>
            </section>
          ) : null}

          <ToolsGrid
            surface="public"
            activeCategory={activeCategory}
            searchQuery={searchQuery}
            onResetFilters={resetFilters}
            onToolOpen={onToolOpen}
          />
        </div>
      </main>
    </MarketingShell>
  );
}

/**
 * W.4.115 (#2686): `pdf editor` is the largest paid keyword, and the grid only
 * holds converters. The Studio is the editor; this card opens it in one click
 * for an editor keyword and for the Edit filter.
 */
function EditorCard() {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="editor-card"
      className="mb-10 flex flex-col gap-4 rounded-xl border border-primary/40 bg-card p-5 shadow-card sm:flex-row sm:items-center sm:p-6"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
        <FilePenLine className="h-6 w-6" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id="editor-card" className="text-xl font-semibold text-foreground">
          {t('tools.index.editorCard.title', { defaultValue: 'Edit a PDF' })}
        </h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {t('tools.index.editorCard.body', {
            defaultValue:
              'Change the text in a PDF, add new text, sign it, and fix its pages. Free, in your browser.',
          })}
        </p>
      </div>
      <Link
        to={STUDIO_EDITOR_HREF}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {t('tools.index.editorCard.cta', { defaultValue: 'Open the PDF editor' })}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </section>
  );
}
